export const base64UrlToBuffer = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  return bytes.buffer;
};

export const bufferToBase64Url = (buffer: ArrayBuffer): string => {
  let binary = "";
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const descriptorsFromJSON = (descriptors?: PublicKeyCredentialDescriptorJSON[]) =>
  descriptors?.map(descriptor => ({ ...descriptor, id: base64UrlToBuffer(descriptor.id) }));

export const creationOptionsFromJSON = (
  json: PublicKeyCredentialCreationOptionsJSON
): PublicKeyCredentialCreationOptions => {
  if (typeof PublicKeyCredential.parseCreationOptionsFromJSON === "function") {
    return PublicKeyCredential.parseCreationOptionsFromJSON(json);
  }

  return {
    ...json,
    challenge: base64UrlToBuffer(json.challenge),
    user: { ...json.user, id: base64UrlToBuffer(json.user.id) },
    excludeCredentials: descriptorsFromJSON(json.excludeCredentials),
  } as PublicKeyCredentialCreationOptions;
};

export const requestOptionsFromJSON = (
  json: PublicKeyCredentialRequestOptionsJSON
): PublicKeyCredentialRequestOptions => {
  if (typeof PublicKeyCredential.parseRequestOptionsFromJSON === "function") {
    return PublicKeyCredential.parseRequestOptionsFromJSON(json);
  }

  return {
    ...json,
    challenge: base64UrlToBuffer(json.challenge),
    allowCredentials: descriptorsFromJSON(json.allowCredentials),
  } as PublicKeyCredentialRequestOptions;
};

const responseToJSON = (response: AuthenticatorResponse): Record<string, unknown> => {
  const json: Record<string, unknown> = { clientDataJSON: bufferToBase64Url(response.clientDataJSON) };

  if ("attestationObject" in response) {
    const attestation = response as AuthenticatorAttestationResponse;
    json.attestationObject = bufferToBase64Url(attestation.attestationObject);
    json.transports = attestation.getTransports?.() ?? [];
  } else {
    const assertion = response as AuthenticatorAssertionResponse;
    json.authenticatorData = bufferToBase64Url(assertion.authenticatorData);
    json.signature = bufferToBase64Url(assertion.signature);
    json.userHandle = assertion.userHandle ? bufferToBase64Url(assertion.userHandle) : null;
  }

  return json;
};

/** The server expects the shape of PublicKeyCredential#toJSON, which older browsers lack. */
export const credentialToJSON = (credential: PublicKeyCredential): Record<string, unknown> => {
  if (typeof credential.toJSON === "function") return credential.toJSON() as Record<string, unknown>;

  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: responseToJSON(credential.response),
  };
};
